import type { ParseKeys } from 'i18next';
import type { MiceNeedAlert, MiceNeedStatus, MiceNeedView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { ToneChip, type ToneChipTone } from '@web/ui/core';

const TONE = {
  validating: 'gray',
  invalid: 'red',
  duplicate: 'gray',
  ready: 'brand',
  recommending: 'ai',
  recommended: 'brand',
  no_solution: 'red',
  reconsidering: 'ai',
  approved: 'green',
  manual: 'amber',
  ordered: 'green',
} as const satisfies Record<MiceNeedStatus, ToneChipTone>;

const STATUS_KEY = {
  validating: 'mice.needs.status.validating',
  invalid: 'mice.needs.status.invalid',
  duplicate: 'mice.needs.status.duplicate',
  ready: 'mice.needs.status.ready',
  recommending: 'mice.needs.status.recommending',
  recommended: 'mice.needs.status.recommended',
  no_solution: 'mice.needs.status.no_solution',
  reconsidering: 'mice.needs.status.reconsidering',
  approved: 'mice.needs.status.approved',
  manual: 'mice.needs.status.manual',
  ordered: 'mice.needs.status.ordered',
} as const satisfies Record<MiceNeedStatus, ParseKeys>;

const ALERT_KEY = {
  changed_from_suggestion: 'mice.needs.alert.changed_from_suggestion',
  savings_review: 'mice.needs.alert.savings_review',
  tolerance_used: 'mice.needs.alert.tolerance_used',
  suggestion_not_found: 'mice.needs.alert.suggestion_not_found',
  suggestion_breaks_premise: 'mice.needs.alert.suggestion_breaks_premise',
  existing_voucher: 'mice.needs.alert.existing_voucher',
  out_of_scope_request: 'mice.needs.alert.out_of_scope_request',
  travel_together: 'mice.needs.alert.travel_together',
  baggage_unknown: 'mice.needs.alert.baggage_unknown',
  price_drift: 'mice.needs.alert.price_drift',
  flight_unavailable: 'mice.needs.alert.flight_unavailable',
  direction_already_ordered: 'mice.needs.alert.direction_already_ordered',
  airport_outside_sheet: 'mice.needs.alert.airport_outside_sheet',
  event_airport_changed: 'mice.needs.alert.event_airport_changed',
  airport_request_unmet: 'mice.needs.alert.airport_request_unmet',
} as const satisfies Record<MiceNeedAlert['code'], ParseKeys>;

export function NeedStatusChip({
  status,
}: Readonly<{ status: MiceNeedStatus }>): React.JSX.Element {
  return <ToneChip tone={TONE[status]}>{t(STATUS_KEY[status])}</ToneChip>;
}

/** Erro ou aviso da necessidade sob o status: âmbar quando ela segue (ex.: cadastro incompleto,
 *  que só barra a compra), vermelho quando a linha não anda. */
export function NeedErrorNote({
  need,
}: Readonly<{ need: Pick<MiceNeedView, 'status' | 'error'> }>): React.JSX.Element | null {
  if (!need.error) return null;
  const blocked = need.status === 'invalid' || need.status === 'duplicate';
  return (
    <span className={`text-meta ${blocked ? 'text-destructive' : 'text-tone-amber-ink'}`}>
      {need.error.message}
    </span>
  );
}

/** Chip curto do alerta; a mensagem completa fica no `title` (e no painel). */
export function NeedAlertChip({ alert }: Readonly<{ alert: MiceNeedAlert }>): React.JSX.Element {
  return (
    <ToneChip tone="amber" title={alert.message}>
      {t(ALERT_KEY[alert.code])}
    </ToneChip>
  );
}
